import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link, useParams, Navigate } from "react-router-dom";
import { useBackNavigation } from "@/hooks/useBackNavigation";

const ActionsDetailPage = () => {
  const { t, isRtl, lang } = useI18n();
  const { slug } = useParams<{ slug: string }>();
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;
  const handleBack = useBackNavigation("/actions");

  const buttonsLabels = {
    parlement: { fr: "Pétitions Parlement", ar: "عرائض البرلمان", zgh: "ⵜⵉⵏⴰⵡⵉⵏ ⵏ ⵓⴱⵕⵍⴰⵎⴰⵏ" },
    avis: { fr: "Donner votre avis sur les textes législatifs", ar: "إبداء رأيك في النصوص التشريعية", zgh: "ⴼⴽ ⴰⵡⴰⵍ ⵏⵏⴽ ⵅⴼ ⵉⴹⵕⵉⵚⵏ ⵏ ⵓⵙⵍⴳⵏ" },
    gouvernement: { fr: "Pétitions Chef du Gouvernement", ar: "عرائض رئيس الحكومة", zgh: "ⵜⵉⵏⴰⵡⵉⵏ ⵏ ⵓⵏⵙⵙⵉⵅⴼ ⵏ ⵜⵏⴱⴰⴹⵜ" }
  };

  const buttonsLinks = {
    parlement: {
      fr: "https://www.chambredesrepresentants.ma/fr/democratie-participative",
      ar: "https://www.chambredesrepresentants.ma/ar/%D8%A7%D9%84%D8%AF%D9%8A%D9%85%D9%82%D8%B1%D8%A7%D8%B7%D9%8A%D8%A9-%D8%A7%D9%84%D8%AA%D8%B4%D8%A7%D8%B1%D9%83%D9%8A%D8%A9-0?q=fr/democratie-participative",
      zgh: "https://www.chambredesrepresentants.ma/fr/democratie-participative"
    },
    avis: {
      fr: "https://www.chambredesrepresentants.ma/fr/donner-votre-avis",
      ar: "https://www.chambredesrepresentants.ma/ar/%D8%A3%D8%B9%D8%B7-%D8%B1%D8%A3%D9%8A%D9%83?q=fr/donner-votre-avis",
      zgh: "https://www.chambredesrepresentants.ma/fr/donner-votre-avis"
    },
    gouvernement: {
      fr: "https://www.eparticipation.ma/fr/ajout-petition",
      ar: "https://www.eparticipation.ma/ajout-petition",
      zgh: "https://www.eparticipation.ma/fr/ajout-petition"
    }
  };

  const action = t.actions.items.find((a) => a.slug === slug);
  if (!action) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="container max-w-3xl">
          <Link to="/actions" onClick={handleBack} className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <BackArrow className="w-4 h-4" />
            {t.common.backHome}
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              {action.title}
            </h1>
            <p className="text-lg text-muted-foreground font-body mb-10">
              {action.description}
            </p>
          </motion.div>

          <motion.div
            className="bg-card rounded-2xl border border-border shadow-card p-8 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ul className="space-y-4">
              {action.content.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground font-body leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {slug === "petitions" && (
            <motion.div
              className="grid gap-4 md:grid-cols-3 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Button asChild className="h-auto min-h-[5rem] py-3 px-4 whitespace-normal text-center flex items-center justify-center font-body gap-2 shadow-card hover:shadow-elevated transition-all">
                <a href={buttonsLinks.parlement[lang as keyof typeof buttonsLinks.parlement]} target="_blank" rel="noopener noreferrer">
                  <span>{buttonsLabels.parlement[lang as keyof typeof buttonsLabels.parlement]}</span>
                  <ExternalLink className="w-4 h-4 shrink-0" />
                </a>
              </Button>
              <Button asChild className="h-auto min-h-[5rem] py-3 px-4 whitespace-normal text-center flex items-center justify-center font-body gap-2 shadow-card hover:shadow-elevated transition-all">
                <a href={buttonsLinks.avis[lang as keyof typeof buttonsLinks.avis]} target="_blank" rel="noopener noreferrer">
                  <span>{buttonsLabels.avis[lang as keyof typeof buttonsLabels.avis]}</span>
                  <ExternalLink className="w-4 h-4 shrink-0" />
                </a>
              </Button>
              <Button asChild className="h-auto min-h-[5rem] py-3 px-4 whitespace-normal text-center flex items-center justify-center font-body gap-2 shadow-card hover:shadow-elevated transition-all">
                <a href={buttonsLinks.gouvernement[lang as keyof typeof buttonsLinks.gouvernement]} target="_blank" rel="noopener noreferrer">
                  <span>{buttonsLabels.gouvernement[lang as keyof typeof buttonsLabels.gouvernement]}</span>
                  <ExternalLink className="w-4 h-4 shrink-0" />
                </a>
              </Button>
            </motion.div>
          )}

          {action.links && action.links.length > 0 && (
            <motion.div
              className="bg-card rounded-2xl border border-border shadow-card p-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-xl font-display font-bold text-foreground mb-6">
                🔗 {t.actions.usefulLinks}
              </h2>
              <div className="space-y-3">
                {action.links.map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 rounded-lg border border-border bg-background hover:shadow-card hover:border-primary/30 transition-all group"
                  >
                    <span className="font-body font-medium text-foreground group-hover:text-primary transition-colors">
                      {link.label}
                    </span>
                    <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </a>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ActionsDetailPage;
